import {Image} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/image.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  BlurryBackground,
  BrandLogo,
  Button,
  COLORS,
  FONT_SIZES,
  GenericIconName,
  HorizontalList,
  HorizontalListWrapper,
  Icon,
  UnstyledLinkLinaria,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
  useInView,
} from '@aboutyou/ui-components';
import {doAsync} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {useTilesSlider} from '../../../../backend-services/BrandHub/utils/useTilesSlider';
import {SLIDER_TIME_S} from './utils/useHeroBrandsSlider';

interface HeroBrandsListProps {
  activeBrandIndex: number;
  brands: Array<{
    id: number;
    name: string;
    darkLogo?: Image;
    lightLogo?: Image;
    fallbackLabel: string;
    image: Image;
  }>;
  onClick: VoidFunction;
  onBrandClick: (index: number) => void;
  onBrandMouseDown: (index: number) => void;
  isPaused: boolean;
  allBrandsUrl?: string;
}

export const HeroBrandsList: React.FC<HeroBrandsListProps> = ({
  activeBrandIndex,
  brands,
  onClick,
  onBrandClick,
  onBrandMouseDown,
  isPaused,
  allBrandsUrl,
}) => {
  const {t} = useTranslation();
  const {
    scrollWrapperRef,
    next,
    prev,
    isNextActive,
    isPrevActive,
    scrollToSlide,
  } = useTilesSlider();

  useEffect(() => {
    doAsync(() => {
      scrollToSlide(activeBrandIndex, {centered: true});
    });
  }, [activeBrandIndex, scrollToSlide]);

  const {ref, inView} = useInView<HTMLDivElement>();

  return (
    <Wrapper
      onClick={onClick}
      onTouchStart={onClick}
      data-testid="heroBrandsList"
      ref={ref}
    >
      {isPrevActive && (
        <PrevCircleButton
          onClick={() => prev()}
          aria-label={t('Go to the previous slide')}
          size="sm"
          round={true}
        >
          <Icon size="XS" asset={GenericIconName.ArrowLeft} />
        </PrevCircleButton>
      )}
      <StyledHorizontalList ref={scrollWrapperRef}>
        {brands.map((brand, index) => {
          const isActive = index === activeBrandIndex;
          const brandImage = isActive ? brand.lightLogo : brand.darkLogo;

          return (
            <BrandWrapper
              key={brand.id}
              data-testid={`heroBrandsListItem-${index}`}
              onClick={() => onBrandClick(index)}
              onMouseDown={() => onBrandMouseDown(index)}
              onTouchStart={() => onBrandMouseDown(index)}
              data-active={isActive && inView}
            >
              <StyledBlurryBackground
                imageUrl={brand.image.url}
                blur={20}
                scale={4}
              />
              <BrandTile
                name={brand.fallbackLabel}
                image={brandImage}
                showTooltip={false}
                maxHeight="100%"
              />
              {isActive && !isPaused && (
                <ProgressBar data-testid={`heroBrandsProgressBar-${index}`} />
              )}
            </BrandWrapper>
          );
        })}
        {allBrandsUrl && (
          <ToAllBrands data-testid="heroBrandsList-ToAllBrands">
            <UnstyledLinkLinaria aria-label={t('All brands')} to={allBrandsUrl}>
              {t('all')}
              <Icon
                asset={GenericIconName.ArrowRight}
                fill={COLORS['Fill.White']}
              />
            </UnstyledLinkLinaria>
          </ToAllBrands>
        )}
      </StyledHorizontalList>
      {isNextActive && (
        <NextCircleButton
          onClick={() => next()}
          aria-label={t('Go to the next slide')}
          size="sm"
          round={true}
        >
          <Icon size="XS" asset={GenericIconName.ArrowRight} />
        </NextCircleButton>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;

  ${tabletAndUpLinaria('padding-right: 16px;')}
`;

const StyledHorizontalList = styled(HorizontalList)`
  display: flex;

  ${HorizontalListWrapper} {
    gap: 8px;
    overflow-y: hidden;
    width: 100%;

    &::after {
      display: none;
    }
  }
`;

const StyledCircleButton = styled(Button)`
  position: absolute;
  z-index: 3;
  display: none;
  justify-content: center;
  align-items: center;
  align-self: center;
  background: ${COLORS.White};
  box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.08),
    0px 3px 12px 2px rgba(0, 0, 0, 0.07);

  ${tabletAndUpLinaria('display: flex;')};
`;

const PrevCircleButton = styled(StyledCircleButton)`
  left: 10px;
`;

const NextCircleButton = styled(StyledCircleButton)`
  right: 10px;
`;

const StyledBlurryBackground = styled(BlurryBackground)`
  opacity: 0;
  transition: opacity 0.3s;

  [data-active='true'] & {
    opacity: 1;
  }

  ${mobileOnlyLinaria(`
    filter: blur(5px);
  `)}
`;

const BrandWrapper = styled.div`
  --width: 56px;
  --height: 56px;
  position: relative;
  display: flex;
  justify-content: center;
  flex: 1 0 var(--width);
  max-width: var(--width);
  height: var(--height);
  border-radius: 4px;
  border: 1px solid ${COLORS.Grey4};
  overflow: hidden;
  cursor: pointer;
  font-size: ${FONT_SIZES.px8};
  text-align: center;

  &[data-active='true'] {
    mix-blend-mode: multiply;
    color: ${COLORS.White};
  }

  ${tabletAndUpLinaria(`
    --width: 205px;
    --height: 90px;
    font-size: ${FONT_SIZES.px14};
  `)};
`;

const BrandTile = styled(BrandLogo)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  z-index: 2;
  padding: 4px;

  ${tabletAndUpLinaria(`
    padding: 16px 32px;
  `)}
`;

const ProgressBar = styled.div`
  position: absolute;
  inset: 0;
  height: 100%;
  transform: scaleX(0);
  transform-origin: left;
  background-color: ${COLORS.White};
  opacity: 0.3;
  z-index: 1;
  animation: enlarge ${SLIDER_TIME_S}s linear;

  @keyframes enlarge {
    0% {
      transform: scaleX(0);
    }
    100% {
      transform: scaleX(1);
    }
  }
`;

const ToAllBrands = styled(BrandWrapper)`
  background: ${COLORS['Fill.Black']};
  color: ${COLORS['Text.White']};

  > a {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1px;
    flex: 1;
  }

  svg {
    --size: 8px;
    width: var(--size);
    height: var(--size);
    flex-basis: var(--size);
  }

  ${tabletAndUpLinaria(`
    > a { gap: 4px; }
    svg { --size: 12px; }
  `)}
`;
